// AcceptInvitePage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Divider
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { auth } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { useLoading } from '../contexts/LoadingContext';
import { API_BASE } from '../config';

const strongPwd = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/;

const AcceptInvitePage = () => {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const { currentUser, getToken } = useAuth();
  const { isLoading, setLoading } = useLoading();
  const navigate = useNavigate();

  const [stage, setStage] = useState('loading'); // loading | expired | create | accept
  const [invite, setInvite] = useState(null);

  // ▼ password / TOS form state (create branch only)
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [tosChecked, setTos] = useState(false);

  const [formErr, setErr] = useState('');

  // ✅ Align with Login.jsx pattern: navigate only once AuthContext is ready
  const [pendingRedirect, setPendingRedirect] = useState(false);

  // ✅ UX: explicit local submission indicator so the user sees feedback immediately
  // (even if global LoadingScreen is not mounted on this route)
  const [submitting, setSubmitting] = useState(false);

  /* ─────────────────────────────────────────────────────
     1) Validate token on mount
  ───────────────────────────────────────────────────── */
  useEffect(() => {
    const run = async () => {
      if (!token) {
        setStage('expired');
        return;
      }

      // ✅ If auth is still resolving, keep the page in loading to avoid flicker
      // Firebase rule:
      //   undefined = auth not resolved yet
      //   null      = signed out
      //   object    = signed in
      if (currentUser === undefined) {
        setStage('loading');
        return;
      }

      try {
        const { data } = await axios.get(
          `${API_BASE}/api/validate-invite?token=${encodeURIComponent(token)}` // 🔹
        );
        setInvite(data);

        const sameUser =
          currentUser &&
          currentUser.email.toLowerCase() === data.email.toLowerCase();

        setStage(sameUser ? 'accept' : 'create');
      } catch {
        setStage('expired');
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, currentUser?.email, currentUser]);

  // ✅ If we signed in / accepted and are waiting for AuthContext hydration, only navigate once currentUser is ready.
  useEffect(() => {
    if (!pendingRedirect) return;

    if (currentUser) {
      navigate('/', { replace: true });
      setPendingRedirect(false);
      setLoading(false, 'AcceptInvite');
      setSubmitting(false);
    }
  }, [pendingRedirect, currentUser, navigate, setLoading]);

  // ✅ Safety cleanup: never leave AcceptInvite loading stuck if we unmount mid-flight
  useEffect(() => {
    return () => {
      setLoading(false, 'AcceptInvite');
    };
  }, [setLoading]);

  /* helpers */
  const toggleShow = () => setShowPwd((v) => !v);

  /* ─────────────────────────────────────────────────────
     2) create-account flow  (invitee not yet registered)
  ───────────────────────────────────────────────────── */
  const createAccount = async () => {
    setErr('');

    if (!strongPwd.test(pwd))
      return setErr('Password must be 8+ chars, upper/lower/numeric/symbol.');
    if (pwd !== confirm) return setErr('Passwords do not match.');
    if (!tosChecked)
      return setErr('You must accept the Terms of Service & Privacy Policy.');

    // ✅ Ensure user sees immediate feedback on click
    setSubmitting(true);
    setLoading(true, 'AcceptInvite');

    try {
      /* 1️⃣  Create the Firebase Auth user on your B-F-F */
      await axios.post(`${API_BASE}/api/create-account`, {
        email: invite.email,
        password: pwd,
        token,
        tosAccepted: true
      });

      /* 2️⃣  **Now** sign in – this gives us an auth token */
      await signInWithEmailAndPassword(auth, invite.email, pwd);

      /* 3️⃣  Record TOS/Privacy acceptance **after** the user is authenticated */
      try {
        /* 3-a) wait until the Auth state observer fires at least once */
        await new Promise((res) =>
          auth.onAuthStateChanged(
            (u) => (u ? res() : null),
            /* error */ () => res() // never block forever
          )
        );

        /* 3-b) now force-refresh the token (guarantees it’s minted *after* sign-in) */
        await auth.currentUser.getIdToken(true);

        /* 3-c) finally call the Cloud Function */
        const recordAcceptance = httpsCallable(functions, 'recordTosAcceptance');
        await recordAcceptance({ version: '1.0' });
        console.log('[AcceptInvite] ✔ TOS/Privacy acceptance recorded.');
      } catch (recErr) {
        console.error('[AcceptInvite] ❌ Failed to record TOS:', recErr);
        // optional: surface toast / schedule retry
      }

      /* 4️⃣  Done – go home
         IMPORTANT:
         Do NOT navigate immediately. Wait for AuthContext hydration to avoid flicker/redirect loops.
      */
      setPendingRedirect(true);
    } catch (e) {
      setErr(e.response?.data?.message || 'Unexpected error.');
      setLoading(false, 'AcceptInvite');
      setPendingRedirect(false);
      setSubmitting(false);
    }
    // IMPORTANT:
    // Do NOT setLoading(false) in a finally block here.
    // If we successfully sign in, we keep the spinner up until AuthContext is ready and we redirect.
  };

  /* ─────────────────────────────────────────────────────
     3) accept-invite flow  (already signed-in user)
  ───────────────────────────────────────────────────── */
  const acceptInvite = async () => {
    setErr('');
    setSubmitting(true);
    setLoading(true, 'AcceptInvite');
    try {
      const idToken = await getToken();
      await axios.post(
        `${API_BASE}/api/accept-invite`, // 🔹
        { token },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      // ✅ Mirror Login.jsx behavior: wait until AuthContext is ready before navigating
      // (Organization/permissions can change as a result of accepting the invite)
      setPendingRedirect(true);
    } catch (e) {
      setErr(e.response?.data?.message || 'Unexpected error.');
      setLoading(false, 'AcceptInvite');
      setPendingRedirect(false);
      setSubmitting(false);
    }
  };

  /* convenience – enable button only when everything valid */
  const canCreate = useMemo(
    () => strongPwd.test(pwd) && pwd === confirm && tosChecked && !isLoading && !submitting,
    [pwd, confirm, tosChecked, isLoading, submitting]
  );

  /* ─────────────────────────────────────────────────────
     4) render
  ───────────────────────────────────────────────────── */
  const logo = process.env.PUBLIC_URL + '/utmlogo.svg';

  const orgLabel = invite?.organizationName || invite?.organizationId || 'your team';
  const inviteEmail = invite?.email || '';
  const inviteRole = invite?.permissions || '';

  // Keep your existing loading/expired behavior intact (logic unchanged)
  if (stage === 'loading') {
    return (
      <Box minHeight="100vh" display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  if (stage === 'expired') {
    return (
      <Box minHeight="100vh" display="flex" justifyContent="center" alignItems="center" p={2}>
        <Paper sx={{ p: 3, maxWidth: 400, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Invitation Link Invalid
          </Typography>
          <Typography variant="body2">
            This invitation link is invalid or has expired. Please contact the administrator and
            request a new invitation link.
          </Typography>
          <Button sx={{ mt: 2 }} variant="contained" component={Link} to="/login">
            Go to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  // Split layout (matches Signup.jsx) for both create + accept stages
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: '#f5f5f5'
      }}
    >
      <Grid container sx={{ width: '100%' }}>
        {/* LEFT SIDE: brand + context (NOT inside a card) */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'center', md: 'center' },
            px: { xs: 3, sm: 6, md: 8 },
            py: { xs: 4, md: 0 },

            // Same Option A + tint as Signup.jsx:
            backgroundColor: 'rgba(72, 157, 255, 0.04)',
            position: 'relative',

            borderRight: { md: '1px solid rgba(0,0,0,0.08)' },

            // Thin brand stripe
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '6px',
              backgroundColor: '#489DFF'
            }
          }}
        >
          <Box sx={{ width: '100%', maxWidth: 520 }}>
            {/* Center ONLY the logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <img src={logo} alt="logo" style={{ maxWidth: 160 }} />
            </Box>

            {/* Left-align everything else */}
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                Welcome!
              </Typography>

              {/* 
                FIX #1 + #2:
                - Force two lines (no "columns" effect when narrow)
                - Ensure spacing between "join" and org name
                - Remove the trailing period issue ("Inc..")
              */}
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 400,
                  mb: 3,
                  lineHeight: 1.35
                }}
              >
                <Box component="span" sx={{ display: 'block' }}>
                  You&apos;ve been invited to join
                </Box>
                <Box component="span" sx={{ display: 'block', color: 'text.primary', fontWeight: 700 }}>
                  {orgLabel}
                </Box>
              </Typography>

              <Box sx={{ display: 'grid', gap: 1.25, mb: 3 }}>
                <Typography variant="body1">✓ Join your team workspace</Typography>
                <Typography variant="body1">
                  ✓ Sign in with <strong>{inviteEmail}</strong>
                </Typography>
                {inviteRole ? (
                  <Typography variant="body1">
                    ✓ Role: <strong>{inviteRole}</strong>
                  </Typography>
                ) : (
                  <Typography variant="body1">✓ Accept and continue</Typography>
                )}
              </Box>

              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Secure, role-based access for your organization.
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* RIGHT SIDE: card UI (keeps your existing AcceptInvitePage content/logic) */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, sm: 4, md: 6 },
            py: { xs: 4, md: 0 }
          }}
        >
          <Paper
            sx={{
              p: 3,
              maxWidth: 520,
              width: '100%',
              textAlign: 'center',

              // Same behavior as Signup.jsx: keep usable on shorter-height viewports
              maxHeight: { xs: 'unset', md: 'calc(100vh - 64px)' },
              overflow: { md: 'auto' }
            }}
            elevation={3}
          >
            {/* IMPORTANT:
                We are keeping your existing UI copy and flow on the right panel,
                and only adapting layout + framing to match the Signup split design.
            */}

            {/* ─────────────────────  CREATE ACCOUNT UI  ───────────────────── */}
            {stage === 'create' && (
              <>
                {/* FIX #3: Remove redundant "Join {orgLabel}" on right panel */}

                {/* FIX #4: Change copy to generic "create your password..." */}
                <Typography variant="body2" gutterBottom>
                  Create your password to access your account.
                </Typography>

                {formErr && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {formErr}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  margin="normal"
                  label="Password"
                  type={showPwd ? 'text' : 'password'}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  disabled={isLoading || submitting}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleShow} disabled={isLoading || submitting}>
                          {showPwd ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Confirm Password"
                  type={showPwd ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={isLoading || submitting}
                />

                {/* Terms & Privacy checkbox */}
                <Box textAlign="left" mt={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={tosChecked}
                        onChange={(e) => setTos(e.target.checked)}
                        disabled={isLoading || submitting}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I agree to the&nbsp;
                        <Link
                          to="https://app.utmsmartmanager.com/terms-and-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms&nbsp;of&nbsp;Service
                        </Link>
                        &nbsp;and&nbsp;
                        <Link
                          to="https://app.utmsmartmanager.com/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Privacy&nbsp;Policy
                        </Link>
                        .
                      </Typography>
                    }
                  />
                  {!tosChecked &&
                    formErr === 'You must accept the Terms of Service & Privacy Policy.' && (
                      <FormHelperText error sx={{ ml: 1 }}>
                        {formErr}
                      </FormHelperText>
                    )}
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  sx={{ mt: 2 }}
                  disabled={!canCreate}
                  onClick={createAccount}
                  startIcon={submitting ? <CircularProgress size={18} /> : null}
                >
                  {submitting ? 'Creating...' : 'Create Account & Join'}
                </Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2">
                  Already have an account? <Link to="/login">Log&nbsp;in</Link>
                </Typography>
              </>
            )}

            {/* ─────────────────────  ACCEPT (already signed-in) UI  ───────────────────── */}
            {stage === 'accept' && (
              <>
                <Typography variant="h6" gutterBottom>
                  Accept Invitation
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Continue to join <strong>{orgLabel}</strong>{' '}
                  {inviteRole ? (
                    <>
                      as a&nbsp;<strong>{inviteRole}</strong>.
                    </>
                  ) : (
                    <>.</>
                  )}
                </Typography>

                {formErr && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {formErr}
                  </Alert>
                )}

                <Button
                  variant="contained"
                  fullWidth
                  disabled={isLoading || submitting}
                  onClick={acceptInvite}
                  startIcon={submitting ? <CircularProgress size={18} /> : null}
                >
                  {submitting ? 'Continuing...' : 'Accept & Continue'}
                </Button>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AcceptInvitePage;
